import React, { useState, useEffect } from 'react';

// Tutorial steps for Solitaire mode
export const SOLITAIRE_TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Neolysium',
    content: 'Create a thriving environment by strategically playing cards to generate energy. Let\'s get started!',
    position: 'center',
  },
  {
    id: 'hand',
    title: 'Your Hand',
    content: 'These are your cards. Tap any card to view its full details along with options to play or discard.',
    position: 'bottom',
    highlight: 'hand',
  },
  {
    id: 'energy',
    title: 'Energy',
    content: 'Energy is your score. Each card contributes positive or negative energy when played. Your goal is to maximize your total.',
    position: 'top',
    highlight: 'energy',
  },
  {
    id: 'spaces',
    title: 'Spaces',
    content: 'You begin with 10 spaces to place cards. Once full, the game ends. Certain cards grant additional spaces.',
    position: 'top',
    highlight: 'spaces',
  },

  {
    id: 'prerequisites',
    title: 'Prerequisites',
    content: 'Many cards require other cards to be played first. A red border means prerequisites haven\'t been met yet.',
    position: 'center',
  },
  {
    id: 'deck',
    title: 'Your Deck',
    content: 'Keep an eye on your remaining cards. The game ends when your deck runs out or your spaces are filled.',
    position: 'top',
    highlight: 'deck',
  },
  {
    id: 'ready',
    title: 'Ready to Play!',
    content: 'Balance your spaces wisely, build powerful card chains, and create the most efficient environment possible. Good luck!',
    position: 'center',
  },
];

// Tutorial steps for Versus mode
export const VERSUS_TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Versus Mode',
    content: 'The player with the most energy at the end wins!',
    position: 'center',
  },
  {
    id: 'turns',
    title: 'Taking Turns',
    content: 'Players alternate turns. A green indicator shows when it\'s your turn to act.',
    position: 'top',
  },
  {
    id: 'your-turn',
    title: 'On Your Turn',
    content: 'Tap a card from your hand to view it, then choose to play it to your board or discard.',
    position: 'bottom',
    highlight: 'hand',
  },
  {
    id: 'shared-cards',
    title: 'Shared Cards',
    content: 'You may play the top card of your opponent\'s discard pile. When you do, it goes to a shared area in the center. Both players can use shared cards as prerequisites, but only you gain its energy.',
    position: 'center',
  },
  {
    id: 'wisdom-rule',
    title: 'The Love Exception',
    content: 'Wisdom cards represent personal growth and cannot be taken from your opponent — except for Love, which can be shared.',
    position: 'center',
  },
  {
    id: 'winning',
    title: 'Victory Condition',
    content: 'The game concludes when both players exhaust their decks or fill their spaces. The highest energy total wins!',
    position: 'center',
  },
  {
    id: 'ready',
    title: 'Time to Compete!',
    content: 'Think ahead, watch your opponent\'s discards for opportunities, and build the most thriving environment. May the best strategist win!',
    position: 'center',
  },
];

// Check if tutorial should be shown
export const shouldShowTutorial = (mode) => {
  const key = mode === 'versus' ? 'versusFirstPlay' : 'solitaireFirstPlay';
  return localStorage.getItem(key) !== 'completed';
};

// Mark tutorial as completed
export const completeTutorial = (mode) => {
  const key = mode === 'versus' ? 'versusFirstPlay' : 'solitaireFirstPlay';
  localStorage.setItem(key, 'completed');
};

// Tutorial Overlay Component
const TutorialOverlay = ({ mode, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  
  const steps = mode === 'versus' ? VERSUS_TUTORIAL_STEPS : SOLITAIRE_TUTORIAL_STEPS;
  const step = steps[currentStep];
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleComplete = () => {
    setIsVisible(false);
    completeTutorial(mode);
    if (onComplete) onComplete();
  };
  
  const handleSkip = () => {
    setIsVisible(false);
    completeTutorial(mode);
    if (onSkip) onSkip();
  };
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isVisible) return;
      
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        handlePrevious();
      } else if (e.key === 'Escape') {
        handleSkip();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });
  
  if (!isVisible) return null;
  
  // Position styles based on step.position
  const getPositionStyle = () => {
    switch (step.position) {
      case 'top':
        return { top: '15%', left: '50%', transform: 'translateX(-50%)' };
      case 'bottom':
        return { bottom: '25%', left: '50%', transform: 'translateX(-50%)' };
      case 'center':
      default:
        return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
  };
  
  return (
    <div 
      role="dialog"
      aria-modal="true"
      aria-label={`${mode === 'versus' ? 'Versus' : 'Solitaire'} Mode Tutorial`}
      aria-describedby="tutorial-content"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(5px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Tutorial Card */}
      <div 
        role="document"
        style={{
          position: 'absolute',
          ...getPositionStyle(),
          width: '90%',
          maxWidth: '400px',
          background: 'linear-gradient(145deg, rgba(20, 20, 40, 0.98) 0%, rgba(30, 30, 60, 0.98) 100%)',
          border: '2px solid rgba(255, 215, 0, 0.5)',
          borderRadius: '20px',
          padding: '25px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(255, 215, 0, 0.2)',
        }}
      >
        {/* Progress indicator */}
        <div 
          role="progressbar"
          aria-valuenow={currentStep + 1}
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-label={`Tutorial progress: step ${currentStep + 1} of ${steps.length}`}
          style={{
            display: 'flex',
            gap: '6px',
            justifyContent: 'center',
            marginBottom: '20px',
          }}
        >
          {steps.map((_, index) => (
            <div
              key={index}
              aria-hidden="true"
              style={{
                width: index === currentStep ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                backgroundColor: index <= currentStep ? '#ffd700' : 'rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>
        
        {/* Step number */}
        <div 
          aria-hidden="true"
          style={{
            fontSize: '12px',
            color: 'rgba(255, 215, 0, 0.7)',
            textAlign: 'center',
            marginBottom: '10px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
          }}
        >
          Step {currentStep + 1} of {steps.length}
        </div>
        
        {/* Title */}
        <h2 
          id="tutorial-title"
          style={{
            margin: 0,
            marginBottom: '15px',
            color: '#ffd700',
            fontSize: '22px',
            fontWeight: 'bold',
            textAlign: 'center',
            textShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
          }}
        >
          {step.title}
        </h2>
        
        {/* Content */}
        <p 
          id="tutorial-content"
          style={{
            margin: 0,
            marginBottom: '25px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '16px',
            lineHeight: '1.6',
            textAlign: 'center',
          }}
        >
          {step.content}
        </p>
        
        {/* Navigation buttons */}
        <nav 
          aria-label="Tutorial navigation"
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
          }}
        >
          {currentStep > 0 && (
            <button
              onClick={handlePrevious}
              aria-label={`Go to previous step: ${steps[currentStep - 1]?.title || 'Previous'}`}
              style={{
                padding: '12px 24px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '25px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            >
              Back
            </button>
          )}
          
          <button
            onClick={handleNext}
            aria-label={currentStep === steps.length - 1 
              ? 'Complete tutorial and start playing' 
              : `Go to next step: ${steps[currentStep + 1]?.title || 'Next'}`}
            style={{
              padding: '12px 32px',
              backgroundColor: '#ffd700',
              border: 'none',
              borderRadius: '25px',
              color: '#000',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
            }}
          >
            {currentStep === steps.length - 1 ? 'Start Playing' : 'Next'}
          </button>
        </nav>
        
        {/* Skip button */}
        <button
          onClick={handleSkip}
          aria-label="Skip tutorial and start playing"
          style={{
            display: 'block',
            margin: '15px auto 0',
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '13px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          Skip Tutorial
        </button>
      </div>
    </div>
  );
};

export default TutorialOverlay;

